import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOrder } from './OrderContext';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { collection, query, where, orderBy, getDocs, Timestamp, doc, getDoc, onSnapshot } from 'firebase/firestore'; // onSnapshotを追加
import { Container, Button, Card, Spinner, Alert, Row, Col, ListGroup, Accordion, Badge } from 'react-bootstrap'; // Badgeを追加

function MainMenu() {
  const navigate = useNavigate();
  const { resetOrder } = useOrder();
  const [userInfo, setUserInfo] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [announcements, setAnnouncements] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showAllAnnouncements, setShowAllAnnouncements] = useState(false);

  // ★追加：メンテナンス状態を管理するState
  const [isMaintenance, setIsMaintenance] = useState(false);

  useEffect(() => {
    document.title = 'メインメニュー | My Order System';
  }, []);

  // ★追加：メンテナンス状態のリアルタイム監視
  useEffect(() => {
    const sessionDocRef = doc(db, "system_settings", "session_control");
    const unsubscribe = onSnapshot(sessionDocRef, (docSnap) => {
      setIsMaintenance(docSnap.exists());
    }, (err) => {
      console.error("Maintenance listener error:", err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        if (user.isAnonymous) {
          setUserInfo({ personName: 'ゲストユーザー', userAttribute: '一般ユーザー', isAnonymous: true });
          setCompanyInfo({ companyName: '(所属なし)' });
        } else {
          try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              setUserInfo({ ...userData, isAnonymous: false });
              if (userData.companyId) {
                const companyDocRef = doc(db, 'companies', userData.companyId);
                const companyDocSnap = await getDoc(companyDocRef);
                if (companyDocSnap.exists()) {
                  setCompanyInfo(companyDocSnap.data());
                } else {
                  setCompanyInfo({ companyName: '(会社情報なし)' });
                }
              }
            } else { setError('ユーザーデータが見つかりません。'); }
          } catch (err) { setError('データの取得に失敗しました。'); console.error(err); }
        }
      }
      setLoading(false);
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    if (!userInfo) return;
    const fetchNewsAndHolidays = async () => {
      try {
        const today = Timestamp.now();
        const newsCollectionRef = collection(db, 'news');
        const q = query(newsCollectionRef, where('startDate', '<=', today), orderBy('startDate', 'desc'));
        const querySnapshot = await getDocs(q);
        const allRecentNews = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        const filteredNews = allRecentNews.filter(news => {
          const isPublished = news.status === 'published';
          const isWithinEndDate = !news.endDate || news.endDate.toDate() >= today.toDate();
          const isTargeted = news.targetAudience && news.targetAudience[userInfo.userAttribute];
          return isPublished && isWithinEndDate && isTargeted;
        });
        setAnnouncements(filteredNews);
      } catch (err) { console.error("ニュースの取得に失敗しました:", err); setError("お知らせの取得に失敗しました。"); }
      try {
        const settingsDocRef = doc(db, 'holiday_settings', 'config');
        const docSnap = await getDoc(settingsDocRef);
        if (docSnap.exists() && docSnap.data().specialHolidays) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const upcomingHolidays = docSnap.data().specialHolidays.filter(holiday => holiday.endDate.toDate() >= today).sort((a, b) => a.startDate.toDate() - b.startDate.toDate());
          setHolidays(upcomingHolidays);
        }
      } catch (err) { console.error("休日設定の取得に失敗しました:", err); }
    };
    fetchNewsAndHolidays();
  }, [userInfo]);


  const handleLogout = async () => {
    try { await signOut(auth); navigate('/login'); } catch (error) { console.error("ログアウトに失敗しました", error); }
  };

  const handleNewOrderClick = () => {
    resetOrder();
    navigate('/new-order');
  };

  const handleOrderHistoryClick = () => {
    if (userInfo?.isAnonymous) {
      alert('発注履歴の閲覧にはユーザー登録が必要です。');
    } else {
      navigate('/order-history');
    }
  };

  const announcementsToShow = showAllAnnouncements ? announcements : announcements.slice(0, 5);

  if (loading) {
    return <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <Spinner animation="border" role="status">
        <span className="visually-hidden">
          読み込み中...
        </span>
      </Spinner>
    </Container>;
  }

  return (
    <main>
      <style>{`
        .custom-accordion .accordion-button {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        .custom-accordion .accordion-item {
          background-color: transparent;
          border: none;
        }
      `}
      </style>

      <Container className="my-4">
        <Card
          className="shadow-sm"
          aria-labelledby="main-menu-title"
        >
          <Card.Body className="p-4">
            <header className="mb-4">
              <h2
                id="main-menu-title"
                className="visually-hidden"
              >
                メインメニュー
              </h2>
              {
                userInfo && (
                  <div>
                    <h4>
                      {userInfo.personName} 様
                    </h4>
                    {
                      companyInfo &&
                      <p className="mb-0">
                        会社名：{companyInfo.companyPrefix}{companyInfo.companyName}{companyInfo.companySuffix}
                      </p>
                    }
                  </div>
                )
              }
            </header>
            {
              error &&
              <Alert
                variant="danger"
                role="alert"
                aria-live="polite"
              >
                {error}
              </Alert>
            }
            {
              userInfo && ['社員', '管理者', '一般ユーザー'].includes(userInfo.userAttribute) &&
              (
                <Row className="g-2 mb-4">
                  <Col md={6}>
                    <Button
                      size="lg"
                      className="w-100 shadow fw-bold"
                      onClick={handleNewOrderClick}
                      style={{ backgroundColor: '#24639c' }}
                    >
                      新規発注
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button
                      variant="white"
                      size="lg"
                      className="btn btn-outline-secondary w-100 shadow"
                      onClick={handleOrderHistoryClick}
                    >
                      発注履歴を見る
                    </Button>
                  </Col>
                </Row>
              )
            }
            <section
              className="p-3 mb-4 bg-warning-subtle border border-warning-subtle rounded shadow-sm"
              aria-labelledby="announcements-title"
            >
              <h3 id="announcements-title">
                お知らせ・休業日のご案内
              </h3>
              {
                announcementsToShow.length > 0 ?
                  (
                    <Accordion
                      flush
                      className="mt-2 custom-accordion"
                    >
                      {announcementsToShow.map(item => (
                        <Accordion.Item
                          eventKey={item.id}
                          key={item.id}
                        >
                          <Accordion.Header>
                            <small className="text-muted me-3">
                              {item.startDate.toDate().toLocaleDateString('ja-JP')}
                            </small>
                            <strong style={{ color: item.titleColor || '#000000' }}>
                              {item.title}
                            </strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p
                              className="mb-0"
                              style={{ color: item.contentColor || '#000000', whiteSpace: 'pre-wrap' }}
                            >
                              {item.content}
                            </p>
                            {
                              item.linkUrl &&
                              item.linkText &&
                              (
                                <div className="text-end">
                                  <a
                                    href={item.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-link p-0 mt-1"
                                  >
                                    {item.linkText}
                                  </a>
                                </div>
                              )
                            }
                          </Accordion.Body>
                          <hr />
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  )
                  :
                  (
                    holidays.length === 0 &&
                    <p className="text-muted mb-0 mt-2">
                      現在、新しいお知らせはありません。
                    </p>
                  )
              }
              {
                announcements.length > 5 &&
                (
                  <div className="text-end mt-2">
                    <Button
                      variant="link"
                      onClick={() => setShowAllAnnouncements(!showAllAnnouncements)}
                    >
                      {
                        showAllAnnouncements ?
                          'ニュースを少なく表示'
                          :
                          'ニュースをもっと見る'
                      }
                    </Button>
                  </div>
                )
              }
            </section>
            <section
              className="p-3 mb-4 bg-light rounded"
              aria-labelledby="support-title"
            >
              <h3 id="support-title">
                アカウント & サポート
              </h3>
              <Row className="g-2 mt-2">
                {
                  userInfo &&
                  ['社員', '管理者', '一般ユーザー'].includes(userInfo.userAttribute) &&
                  (
                    <>
                      {
                        !userInfo?.isAnonymous &&
                        (
                          <>
                            <Col xs={12} md={6}>
                              <Link
                                to="/profile"
                                className="btn btn-outline-secondary w-100 shadow"
                              >
                                ユーザー情報変更
                              </Link>
                            </Col>
                            <Col xs={12} md={6}>
                              <Link
                                to="/shipping-addresses"
                                className="btn btn-outline-secondary w-100 shadow"
                              >
                                送付先リスト登録
                              </Link>
                            </Col>
                          </>
                        )
                      }
                      <Col xs={12} md={6}>
                        <Link
                          to="/contact"
                          className="btn btn-outline-secondary w-100 shadow"
                        >
                          お問い合わせ/ヘルプ
                        </Link>
                      </Col>
                    </>
                  )
                }
                <Col xs={12} md={6}>
                  <Button
                    variant="secondary"
                    className="w-100 shadow text-white"
                    onClick={handleLogout}
                    style={{ backgroundColor: '#676867' }}
                  >
                    ログアウト
                  </Button>
                </Col>
              </Row>
            </section>
            {
              userInfo &&
              ['社員', '管理者'].includes(userInfo.userAttribute) &&
              (
                <section
                  className="p-3 mb-4 bg-light rounded"
                  aria-labelledby="employee-menu-title"
                >
                  <h3 id="employee-menu-title">
                    社員メニュー
                  </h3>
                  <Row className="g-2 mt-1">
                    <Col xs={12} md={6}>
                      <Link
                        to="/customer-follow"
                        className="btn btn-outline-secondary w-100 shadow"
                      >
                        顧客フォロー
                      </Link>
                    </Col>
                    <Col xs={12} md={6}>
                      <Link
                        to="/employee/order-search"
                        className="btn btn-outline-secondary w-100 shadow"
                      >
                        社員向け注文履歴検索
                      </Link>
                    </Col>
                  </Row>
                </section>
              )
            }
            {
              userInfo &&
              ['出荷担当者', '管理者'].includes(userInfo.userAttribute) &&
              (
                <section
                  className="p-3 mb-4 bg-light rounded"
                  aria-labelledby="shipper-menu-title"
                >
                  <h3 id="shipper-menu-title">
                    出荷担当者メニュー
                  </h3>
                  <Row className="g-2 mt-1">
                    <Col xs={12} md={6}>
                      <Button
                        variant="outline-secondary"
                        className="w-100 shadow"
                        onClick={() => navigate('/shipper-menu')}
                      >
                        出荷者メニュー
                      </Button>
                    </Col>
                  </Row>
                </section>
              )
            }
            {
              userInfo &&
              userInfo.userAttribute === '管理者' &&
              (
                <section
                  className="p-3 mb-4 bg-light rounded shadow-sm border border-secondary-subtle"
                  aria-labelledby="admin-menu-title"
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h3 id="admin-menu-title" className="mb-0">
                      管理者メニュー
                    </h3>
                    {/* ★追加：現在のメンテナンスステータスを表示 */}
                    {isMaintenance ? (
                      <Badge bg="danger" className="p-2">メンテナンス実施中</Badge>
                    ) : (
                      <Badge bg="success" className="p-2">通常稼働中</Badge>
                    )}
                  </div>
                  <Row className="g-2 mt-1">
                    <Col xs={12} md={6}>
                      <Button
                        variant="outline-secondary"
                        className="w-100 shadow"
                        onClick={() => navigate('/admin')}
                      >
                        管理者メニュー
                      </Button>
                    </Col>
                  </Row>
                </section>
              )
            }

            <footer className="mt-5 pt-4 border-top text-center">
              <Row className="justify-content-center">
                <Col xs="auto">
                  <Link
                    to="/privacy-policy"
                    className="text-muted small mx-2"
                  >
                    プライバシーポリシー
                  </Link>
                </Col>
              </Row>
              <p className="text-muted small mt-3 mb-0">
                &copy; {new Date().getFullYear()} 旭興・西武WEBサンプル受注システム. All Rights Reserved.
              </p>
              <p className="text-muted small mt-3 mb-0">3.0.1</p>
            </footer>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default MainMenu;